<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import OpportunitySocialLinks from './OpportunitySocialLinks.vue';
import { statusMeta } from 'dashboard/modules/kanban/constants/statusByCategory.js';

const props = defineProps({
  card: { type: Object, required: true },
  title: { type: String, default: '' },
  status: { type: String, default: 'open' },
  lossReason: { type: String, default: '' },
  waitingReason: { type: String, default: '' },
  aiEnabled: { type: Boolean, default: false },
  pickingLossReason: { type: Boolean, default: false },
  pickingWaitingReason: { type: Boolean, default: false },
  pipelineCategory: { type: String, default: 'sales' },
});

const emit = defineEmits([
  'close',
  'open-contact',
  'update:title',
  'update:status',
  'pickLossReason',
  'pickWaitingReason',
  'pickWonValue',
  'title-enter',
]);

const { t } = useI18n();

const isEditingTitle = ref(false);
const isEditingSocialLink = ref(false);
const showStatusSelect = ref(false);
const statusBadgeRef = ref(null);
const wonValueRef = ref(null);

const localTitle = ref(props.title);
const titleInputRef = ref(null);

watch(
  () => props.title,
  v => {
    if (!isEditingTitle.value) localTitle.value = v;
  }
);

const contact = computed(() => props.card.contact ?? {});

const isPostSales = computed(() => props.pipelineCategory === 'post_sales');

const statusOptions = computed(() =>
  isPostSales.value
    ? [
        {
          value: 'open',
          label: t('KANBAN.CONTACT_SECTION.STATUS_OPEN_POST_SALES'),
          colorClass: 'text-n-blue-11',
        },
        {
          value: 'waiting',
          label: t('KANBAN.CONTACT_SECTION.STATUS_WAITING'),
          colorClass: 'text-n-amber-11',
        },
        {
          value: 'won',
          label: t('KANBAN.CONTACT_SECTION.STATUS_WON_POST_SALES'),
          colorClass: 'text-n-teal-11',
        },
        {
          value: 'lost',
          label: t('KANBAN.CONTACT_SECTION.STATUS_LOST_POST_SALES'),
          colorClass: 'text-n-ruby-11',
        },
      ]
    : [
        {
          value: 'open',
          label: t('KANBAN.CONTACT_SECTION.STATUS_OPEN'),
          colorClass: 'text-n-blue-11',
        },
        {
          value: 'won',
          label: t('KANBAN.CONTACT_SECTION.STATUS_WON'),
          colorClass: 'text-n-teal-11',
        },
        {
          value: 'lost',
          label: t('KANBAN.CONTACT_SECTION.STATUS_LOST'),
          colorClass: 'text-n-ruby-11',
        },
      ]
);

const BADGE_CLASSES = {
  slate: 'bg-n-slate-3 text-n-slate-11',
  blue: 'bg-n-blue-3 text-n-blue-11',
  amber: 'bg-n-amber-3 text-n-amber-11',
  teal: 'bg-n-teal-3 text-n-teal-11',
  ruby: 'bg-n-ruby-3 text-n-ruby-11',
};

const effectiveStatus = computed(() => {
  if (props.pickingLossReason) return 'lost';
  if (props.pickingWaitingReason) return 'waiting';
  return props.status;
});

const statusBadgeClass = computed(() => {
  const { color } = statusMeta(props.pipelineCategory, effectiveStatus.value);
  return BADGE_CLASSES[color] ?? BADGE_CLASSES.slate;
});

const statusLabel = computed(() => {
  return (
    statusOptions.value.find(s => s.value === effectiveStatus.value)?.label ??
    effectiveStatus.value
  );
});

const stalledDays = computed(() => {
  if (props.status !== 'open' && props.status !== 'waiting') return null;
  const refTs =
    props.card.stage_changed_at ?? props.card.last_activity_at ?? null;
  if (!refTs) return null;
  const ms = Date.now() - (refTs > 1e10 ? refTs : refTs * 1000);
  return Math.floor(ms / 86400000);
});

const stalledClass = computed(() => {
  if (stalledDays.value === null) return '';
  if (stalledDays.value >= 10) return 'text-n-ruby-9';
  if (stalledDays.value >= 5) return 'text-n-amber-10';
  return 'text-n-slate-10';
});

const priority = computed(() => props.card.conversation?.priority ?? null);

const priorityMeta = computed(() => {
  const colorMap = {
    urgent: { bars: [true, true, true], fill: 'rgb(var(--ruby-9))' },
    high: { bars: [true, true, false], fill: 'rgb(var(--amber-9))' },
    medium: { bars: [true, true, false], fill: 'rgb(var(--amber-10))' },
    low: { bars: [true, false, false], fill: 'rgb(var(--slate-8))' },
  };
  return priority.value ? (colorMap[priority.value] ?? null) : null;
});

const inactiveFill = 'rgb(var(--slate-4))';

const wonValue = computed(() => {
  const v = props.card.value;
  if (v == null || v === '') return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(v);
});

function formatCardDate(ts) {
  if (!ts) return null;
  return new Date(ts > 1e10 ? ts : ts * 1000).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

const statusChangedDate = computed(() =>
  formatCardDate(props.card.status_changed_at ?? props.card.updated_at)
);

function commitTitle() {
  isEditingTitle.value = false;
  if (localTitle.value !== props.title) emit('update:title', localTitle.value);
}

function selectStatus(val) {
  showStatusSelect.value = false;
  if (val !== props.status) {
    emit('update:status', val);
    if (val === 'lost') {
      const rect = statusBadgeRef.value?.getBoundingClientRect() ?? null;
      emit('pickLossReason', rect);
    } else if (val === 'waiting') {
      const rect = statusBadgeRef.value?.getBoundingClientRect() ?? null;
      emit('pickWaitingReason', rect);
    } else if (val === 'won') {
      nextTick(() => {
        const rect =
          wonValueRef.value?.getBoundingClientRect() ??
          statusBadgeRef.value?.getBoundingClientRect() ??
          null;
        emit('pickWonValue', rect);
      });
    }
  }
}

function focusTitle() {
  isEditingTitle.value = true;
  nextTick(() => {
    titleInputRef.value?.select();
  });
}

defineExpose({ focusTitle });
</script>

<template>
  <div
    class="px-5 pt-5 border-b border-n-weak"
    :class="isEditingSocialLink ? 'pb-8' : 'pb-4'"
  >
    <div class="flex items-start justify-between gap-3 flex-wrap">
      <!-- Avatar + bolinha IA -->
      <div class="relative flex-shrink-0">
        <div
          class="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold bg-n-brand overflow-hidden cursor-pointer"
          :title="t('KANBAN.OPPORTUNITY_MODAL.GO_TO_CONTACT')"
          @click="emit('open-contact')"
        >
          <img
            v-if="contact.avatar_url"
            :src="contact.avatar_url"
            class="w-9 h-9 rounded-full object-cover"
            :alt="contact.name"
          />
          <span v-else>{{ contact.name?.[0]?.toUpperCase() ?? '?' }}</span>
        </div>
        <span
          v-if="aiEnabled"
          class="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-n-teal-9 ring-2 ring-n-background"
          :title="t('KANBAN.OPPORTUNITY_MODAL.AI_ACTIVE')"
        />
      </div>

      <!-- Título + empresa -->
      <div class="flex-1 min-w-[140px]">
        <input
          ref="titleInputRef"
          v-model="localTitle"
          type="text"
          :readonly="!isEditingTitle"
          class="block w-full !text-base leading-snug !font-bold text-n-slate-12 !bg-transparent !outline-none !shadow-none !p-0 !m-0 !h-auto !rounded-none transition-colors"
          :class="
            isEditingTitle
              ? '!border-t-0 !border-l-0 !border-r-0 border-b border-n-weak cursor-text'
              : '!border-none truncate cursor-pointer hover:text-n-brand'
          "
          @input="emit('update:title', localTitle)"
          @blur="commitTitle"
          @keydown.enter="
            commitTitle();
            emit('title-enter');
          "
          @keydown.escape="isEditingTitle = false"
          @click="
            if (!isEditingTitle) {
              isEditingTitle = true;
              localTitle = title;
              $nextTick(() => titleInputRef?.select());
            }
          "
        />

        <OpportunitySocialLinks
          :card="card"
          class="mt-1.5"
          @editing="isEditingSocialLink = $event"
        />
      </div>

      <!-- Fechar + badge de status + contexto -->
      <div class="flex flex-row-reverse items-start gap-2 flex-shrink-0">
        <button
          class="text-n-slate-10 hover:text-n-slate-12 transition-colors mt-0.5"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>

        <div class="flex flex-col items-end gap-1">
          <!-- Badge de status -->
          <div class="relative">
            <button
              ref="statusBadgeRef"
              class="text-[13px] font-medium px-3 py-1 rounded-full transition-opacity"
              :class="[
                statusBadgeClass,
                pickingLossReason || pickingWaitingReason
                  ? 'cursor-default'
                  : 'cursor-pointer hover:opacity-80',
              ]"
              @click="
                !pickingLossReason &&
                  !pickingWaitingReason &&
                  (showStatusSelect = !showStatusSelect)
              "
            >
              {{ statusLabel }}
            </button>
            <div
              v-if="
                showStatusSelect && !pickingLossReason && !pickingWaitingReason
              "
              class="absolute right-0 top-8 z-10 bg-n-background border border-n-weak rounded-lg shadow-lg py-1 min-w-[100px]"
            >
              <button
                v-for="opt in statusOptions"
                :key="opt.value"
                class="w-full text-left px-3 py-1.5 text-xs hover:bg-n-alpha-1 transition-colors font-medium"
                :class="opt.colorClass"
                @click="selectStatus(opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Contexto open: dias parados + prioridade -->
          <div
            v-if="status === 'open' && (stalledDays !== null || priorityMeta)"
            class="flex items-center gap-1.5 justify-end"
          >
            <span
              v-if="stalledDays !== null"
              class="flex items-center gap-1 text-[11px]"
              :class="stalledClass"
            >
              <span class="i-lucide-clock size-3" />
              {{ stalledDays }}
              {{
                stalledDays === 1
                  ? t('KANBAN.CARD.STALLED_DAY')
                  : t('KANBAN.CARD.STALLED_DAYS')
              }}
            </span>
            <span
              v-if="stalledDays !== null && priorityMeta"
              class="w-px h-2.5 bg-n-weak"
            />
            <svg
              v-if="priorityMeta"
              width="13"
              height="11"
              viewBox="0 0 16 14"
              fill="none"
            >
              <rect
                x="0"
                y="6"
                width="3"
                height="8"
                rx="1"
                :fill="priorityMeta.bars[0] ? priorityMeta.fill : inactiveFill"
              />
              <rect
                x="5"
                y="3"
                width="3"
                height="11"
                rx="1"
                :fill="priorityMeta.bars[1] ? priorityMeta.fill : inactiveFill"
              />
              <rect
                x="10"
                y="0"
                width="3"
                height="14"
                rx="1"
                :fill="priorityMeta.bars[2] ? priorityMeta.fill : inactiveFill"
              />
            </svg>
          </div>

          <!-- Contexto lost: motivo + data -->
          <div
            v-else-if="status === 'lost'"
            class="flex items-center gap-1.5 justify-end"
          >
            <span
              class="text-[11px] text-n-ruby-10 cursor-pointer hover:underline truncate max-w-[120px]"
              @click="
                emit(
                  'pickLossReason',
                  $event.currentTarget.getBoundingClientRect()
                )
              "
            >
              {{
                lossReason ||
                (isPostSales
                  ? t('KANBAN.OPPORTUNITY_MODAL.CANCEL_REASON_PLACEHOLDER')
                  : t('KANBAN.OPPORTUNITY_MODAL.LOSS_REASON_PLACEHOLDER'))
              }}
            </span>
            <span
              v-if="statusChangedDate"
              class="w-px h-2.5 bg-n-weak flex-shrink-0"
            />
            <span
              v-if="statusChangedDate"
              class="text-[11px] text-n-slate-9 flex-shrink-0"
            >
              {{ statusChangedDate }}
            </span>
          </div>

          <!-- Contexto waiting: motivo + data -->
          <div
            v-else-if="status === 'waiting'"
            class="flex items-center gap-1.5 justify-end"
          >
            <span
              class="text-[11px] text-n-amber-10 cursor-pointer hover:underline truncate max-w-[120px]"
              @click="
                emit(
                  'pickWaitingReason',
                  $event.currentTarget.getBoundingClientRect()
                )
              "
            >
              {{
                waitingReason ||
                t('KANBAN.OPPORTUNITY_MODAL.WAITING_REASON_PLACEHOLDER')
              }}
            </span>
            <span
              v-if="statusChangedDate"
              class="w-px h-2.5 bg-n-weak flex-shrink-0"
            />
            <span
              v-if="statusChangedDate"
              class="text-[11px] text-n-slate-9 flex-shrink-0"
            >
              {{ statusChangedDate }}
            </span>
          </div>

          <!-- Contexto won: valor + data -->
          <div
            v-else-if="status === 'won'"
            class="flex items-center gap-1.5 justify-end"
          >
            <span
              ref="wonValueRef"
              class="text-[11px] font-semibold text-n-teal-10"
            >
              {{ wonValue ?? '—' }}
            </span>
            <span
              v-if="statusChangedDate"
              class="w-px h-2.5 bg-n-weak flex-shrink-0"
            />
            <span
              v-if="statusChangedDate"
              class="text-[11px] text-n-slate-9 flex-shrink-0"
            >
              {{ statusChangedDate }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
